import React, { useEffect, useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle2,
} from "lucide-react";
import Header from "../components/Layout/Header";
import SiteFooter from "../components/Layout/SiteFooter";
import Reveal from "../components/UI/Reveal";
import useSEO from "../hooks/useSEO";

const contactInfo = [
  {
    icon: Mail,
    label: "Email",
    value: "support@mandapai.in",
    href: "mailto:support@mandapai.in",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+91 90000 00000",
    href: "tel:+919000000000",
  },
  {
    icon: MapPin,
    label: "Office",
    value: "Visakhapatnam, Andhra Pradesh",
  },
  {
    icon: Clock,
    label: "Support hours",
    value: "Mon–Sat, 9am – 7pm IST",
  },
];

const Contact = () => {
  useSEO({
    title: "Contact Us",
    description:
      "Get in touch with the MandapAI team — email support@mandapai.in for help with vendor onboarding, bookings, or partnerships.",
    path: "/contact",
  });
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setSent(true);
    setFormData({ name: "", email: "", message: "" });
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-400 focus:ring-4 focus:ring-purple-50 transition-all";

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 sm:pt-20">
        <Reveal className="max-w-2xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-purple-600" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-600">
              Contact
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight leading-[1.1] mb-4">
            Get in touch
          </h1>
          <p className="text-base sm:text-lg text-gray-500 leading-relaxed">
            Questions about becoming a vendor, a booking, or the platform? Send us
            a message and our team will get back to you.
          </p>
        </Reveal>
      </section>

      {/* Content */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Info cards */}
          <Reveal className="lg:col-span-5 grid sm:grid-cols-2 gap-4">
            {contactInfo.map((c, i) => {
              const inner = (
                <>
                  <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center mb-3">
                    <c.icon className="w-5 h-5" />
                  </div>
                  <div className="text-xs font-semibold tracking-wide uppercase text-gray-400 mb-1">
                    {c.label}
                  </div>
                  <div className="text-sm font-medium text-gray-800 break-words">
                    {c.value}
                  </div>
                </>
              );
              return c.href ? (
                <a
                  key={i}
                  href={c.href}
                  className="rounded-2xl border border-gray-200 p-5 hover:border-gray-300 hover:shadow-lg transition-all duration-300"
                >
                  {inner}
                </a>
              ) : (
                <div key={i} className="rounded-2xl border border-gray-200 p-5">
                  {inner}
                </div>
              );
            })}
          </Reveal>

          {/* Form */}
          <Reveal delay={120} className="lg:col-span-7">
            <div className="rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8">
              {sent ? (
                <div className="flex flex-col items-center text-center py-10">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-7 h-7 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    Message sent!
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    Thanks for reaching out — we'll get back to you shortly.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="text-sm font-semibold text-purple-600 hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-1.5"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1.5"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className={inputClass}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1.5"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      placeholder="How can we help?"
                      className={inputClass}
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-3.5 rounded-xl text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 transition-colors duration-300"
                  >
                    Send Message
                    <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Contact;
